"use strict";

var _Object$defineProperty = require("@babel/runtime-corejs3/core-js/object/define-property");
var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
_Object$defineProperty(exports, "__esModule", {
  value: true
});
exports.renamePasskeyCredential = exports.registerPasskey = exports.getPasskeyCredentials = exports.deletePasskeyCredential = exports.deleteAllPasskeyCredentials = exports.declinePasskeySetup = exports.authenticateWithPasskey = void 0;
var _trim = _interopRequireDefault(require("@babel/runtime-corejs3/core-js/instance/trim"));
var _constants = require("../constants");
var _webauthnUtils = require("../utils/webauthn-utils");
var _passkeyApi = require("./passkey-api");
/**
 * Passkey service functions
 *
 * Individual functions for authentication, registration, and credential management.
 * Each function handles the complete flow including WebAuthn API calls and backend integration.
 *
 * Components can import only the functions they need with their own state management for maximum flexibility.
 */

// Internal helpers for reuse across functions
const ensureSupported = () => {
  if (!(0, _webauthnUtils.isPasskeySupported)()) {
    const error = new Error('Passkeys are not supported in this browser');
    error.name = 'NotSupportedError';
    throw error;
  }
};
const lookupErrorMessage = (err, map) => {
  if (!(err instanceof Error)) {
    return null;
  }
  if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
    return null;
  }
  if (err.name === 'PasskeyAuthenticationCompleteError' || err.name === 'PasskeyRegistrationCompleteError') {
    const backendErrorCode = err.extraResponseData?.errorCode;
    if (backendErrorCode !== undefined && backendErrorCode !== null) {
      const key = String(backendErrorCode);
      if (map[key]) {
        const e = new Error(map[key]);
        e.name = key;
        window.setMessage?.(e.message, 'error');
        return e;
      }
    }
  }
  const httpErrorCode = err?.response?.data?.errorCode;
  if (httpErrorCode !== null) {
    const key = String(httpErrorCode);
    const mapped = map[key];
    if (mapped) {
      const e = new Error(mapped);
      e.name = key;
      window.setMessage?.(e.message, 'error');
      return e;
    }
  }
  const message = map[err.name];
  if (!message) {
    return null;
  }
  const e = new Error(message);
  e.name = err.name;
  window.setMessage?.(e.message, 'error');
  return e;
};
const REGISTER_ERROR_MAP = {
  NotAllowedError: 'Passkey creation was cancelled or not allowed',
  InvalidStateError: 'A passkey already exists for this account',
  AbortError: 'Passkey creation was aborted',
  PasskeyRegistrationStartError: 'Failed to start passkey registration',
  PasskeyRegistrationCompleteError: 'Failed to complete passkey registration'
};
const AUTH_ERROR_MAP = {
  NotAllowedError: 'Passkey authentication was cancelled or not allowed',
  InvalidStateError: 'No passkey is available for this account',
  AbortError: 'Passkey authentication was aborted',
  PasskeyAuthenticationStartError: 'Failed to start passkey authentication',
  PasskeyAuthenticationCompleteError: 'Failed to complete passkey authentication',
  9: window.jQuery ? window.$.__('You may not log in. This account has been disabled. Please contact your administrator to enable access.') : 'You may not log in. This account has been disabled. Please contact your administrator to enable access.',
  19: window.jQuery ? window.$.__('Something went wrong. Please try again later.') : 'Something went wrong. Please try again later.',
  20: window.jQuery ? window.$.__('Your passkey request took too long. Please try again.') : 'Your passkey request took too long. Please try again.',
  21: window.jQuery ? window.$.__("We couldn't verify your passkey. Please try again.") : "We couldn't verify your passkey. Please try again.",
  22: window.jQuery ? window.$.__("We couldn't verify your passkey for this account. Please try another passkey or contact your administrator if the issue continues.") : "We couldn't verify your passkey for this account. Please try another passkey or contact your administrator if the issue continues."
};
const validateName = name => {
  const trimmed = (0, _trim.default)(name).call(name);
  if (!trimmed) {
    const error = new Error('Credential name cannot be empty');
    error.name = 'ValidationError';
    throw error;
  }
  return trimmed;
};
const declinePasskeySetup = () => {
  localStorage.setItem(_constants.hasDeclinedPasskeySetupLocalStorageKey, 'true');
};

/**
 * Registers a new passkey for the current user
 *
 * @param options - Optional configuration for passkey creation
 * @throws Error if WebAuthn is not supported or registration fails
 */
exports.declinePasskeySetup = declinePasskeySetup;
const registerPasskey = async (options = {}) => {
  ensureSupported();
  try {
    // Step 1: Start registration process and get options from server
    const startResponse = await (0, _passkeyApi.startPasskeyRegistration)();

    // Step 2: Prepare options for WebAuthn API
    const credentialCreationOptions = (0, _webauthnUtils.prepareCredentialCreationOptions)(startResponse.options);

    // Step 3: Create credential using WebAuthn API
    const credential = await window.navigator.credentials.create({
      publicKey: credentialCreationOptions
    });
    if (!credential) {
      throw new Error('Failed to create passkey credential');
    }

    // Step 4: Prepare credential data for server
    const registrationData = (0, _webauthnUtils.prepareCredentialForRegistration)(credential);

    // Step 5: Complete registration with server
    return await (0, _passkeyApi.completePasskeyRegistration)(registrationData);
  } catch (err) {
    if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
      throw err;
    }
    const message = lookupErrorMessage(err, REGISTER_ERROR_MAP);
    if (message) {
      throw message;
    }
    throw err;
  }
};

/**
 * Authenticates a user using their passkey
 *
 * @returns Promise containing authentication response
 * @throws Error if WebAuthn is not supported or authentication fails
 */
exports.registerPasskey = registerPasskey;
const authenticateWithPasskey = async (shouldRedirect = true) => {
  ensureSupported();
  try {
    // Step 1: Start authentication process and get options from server
    const startResponse = await (0, _passkeyApi.startPasskeyAuthentication)();

    // Step 2: Prepare options for WebAuthn API
    const credentialRequestOptions = (0, _webauthnUtils.prepareCredentialRequestOptions)(startResponse.options);

    // Step 3: Get credential using WebAuthn API
    const credential = await window.navigator.credentials.get({
      publicKey: credentialRequestOptions
    });
    if (!credential) {
      throw new Error('Failed to get passkey credential');
    }

    // Step 4: Prepare credential data for server
    const authenticationData = (0, _webauthnUtils.prepareCredentialForAuthentication)(credential, startResponse.token);

    // Step 5: Complete authentication with server
    const result = await (0, _passkeyApi.completePasskeyAuthentication)(authenticationData);

    // Handle redirect if present in the response
    if (shouldRedirect && result.redirectUrl) {
      window.location.href = result.redirectUrl;
    }
    return result;
  } catch (err) {
    if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
      throw err;
    }
    const message = lookupErrorMessage(err, AUTH_ERROR_MAP);
    if (message) {
      throw message;
    }
    throw err;
  }
};

/**
 * Loads all passkey credentials for the current user
 *
 * @param userId - The ID of the user whose credentials to retrieve
 * @returns Promise containing array of credential information
 * @throws Error if loading fails
 */
exports.authenticateWithPasskey = authenticateWithPasskey;
const getPasskeyCredentials = async userId => {
  try {
    const response = await (0, _passkeyApi.getPasskeys)(userId);
    return response.credentials;
  } catch (err) {
    if (err instanceof Error && err.name === 'PasskeysGetError') {
      const error = new Error('Failed to load passkey credentials');
      error.name = err.name;
      throw error;
    }
    throw err;
  }
};

/**
 * Renames a passkey credential
 *
 * @param userId - ID of the user who owns the credential
 * @param credentialId - ID of the credential to rename
 * @param newName - New name for the credential
 * @throws Error if name is empty or rename fails
 */
exports.getPasskeyCredentials = getPasskeyCredentials;
const renamePasskeyCredential = async (userId, credentialId, newName) => {
  const trimmed = validateName(newName);
  try {
    await (0, _passkeyApi.renamePasskey)(userId, credentialId, trimmed);
  } catch (err) {
    if (err instanceof Error && err.name === 'PasskeyRenameError') {
      const error = new Error('Failed to rename passkey credential');
      error.name = err.name;
      throw error;
    }
    throw err;
  }
};

/**
 * Deletes a single passkey credential
 *
 * @param userId - ID of the user who owns the credential
 * @param credentialId - ID of the credential to delete
 * @throws Error if deletion fails
 */
exports.renamePasskeyCredential = renamePasskeyCredential;
const deletePasskeyCredential = async (userId, credentialId) => {
  try {
    await (0, _passkeyApi.deletePasskey)(userId, credentialId);
  } catch (err) {
    if (err instanceof Error && err.name === 'PasskeyDeleteError') {
      const error = new Error('Failed to delete passkey credential');
      error.name = err.name;
      throw error;
    }
    throw err;
  }
};

/**
 * Deletes all passkey credentials for a user
 *
 * @param userId - ID of the user whose credentials to delete
 * @throws Error if deletion fails
 */
exports.deletePasskeyCredential = deletePasskeyCredential;
const deleteAllPasskeyCredentials = async userId => {
  try {
    await (0, _passkeyApi.deleteAllPasskeys)(userId);
  } catch (err) {
    if (err instanceof Error && err.name === 'PasskeysDeleteAllError') {
      const error = new Error('Failed to delete all passkey credentials');
      error.name = err.name;
      throw error;
    }
    throw err;
  }
};
exports.deleteAllPasskeyCredentials = deleteAllPasskeyCredentials;